import React from "react";
import { Link } from "react-router-dom";
import { Badge } from "../../../design/badge";
import { Attorney, getAttorneyDisplayName } from "@visitproof/types";
import {
  PhoneIcon,
  EnvelopeIcon,
  BuildingOfficeIcon,
  BriefcaseIcon,
} from "@heroicons/react/20/solid";

interface AttorneyGridProps {
  attorneys: Attorney[];
}

const AttorneyGrid: React.FC<AttorneyGridProps> = ({ attorneys }) => {
  return (
    <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
      {attorneys.map((attorney) => (
        <AttorneyCard key={attorney.id} attorney={attorney} />
      ))}
    </div>
  );
};

interface AttorneyCardProps {
  attorney: Attorney;
}

const AttorneyCard: React.FC<AttorneyCardProps> = ({ attorney }) => {
  const displayName = getAttorneyDisplayName(attorney);
  const isActive = attorney.status === "active";

  return (
    <Link
      to={`/attorney/${attorney.id}`}
      className="group block p-5 bg-white border rounded-xl shadow-sm transition-all duration-200 hover:shadow-md hover:border-blue-300 dark:bg-zinc-900 dark:border-zinc-700 dark:hover:border-blue-600"
    >
      {/* Header */}
      <div className="flex items-start justify-between gap-3 mb-4">
        <div className="flex-1 min-w-0">
          <h3 className="font-semibold text-zinc-900 dark:text-white group-hover:text-blue-600 dark:group-hover:text-blue-400 transition-colors truncate">
            {displayName}
          </h3>
          {attorney.firmName && (
            <p className="text-sm text-zinc-500 dark:text-zinc-400 truncate mt-0.5">
              {attorney.firmName}
            </p>
          )}
        </div>
        <Badge color={isActive ? "green" : "zinc"}>
          {isActive ? "Active" : "Inactive"}
        </Badge>
      </div>

      {/* Contact Info */}
      <div className="space-y-2 mb-4">
        {attorney.phoneNumber && (
          <div className="flex items-center gap-2 text-sm text-zinc-600 dark:text-zinc-400">
            <PhoneIcon className="w-4 h-4 text-zinc-400" />
            <span className="truncate">{attorney.phoneNumber}</span>
          </div>
        )}
        {attorney.email && (
          <div className="flex items-center gap-2 text-sm text-zinc-600 dark:text-zinc-400">
            <EnvelopeIcon className="w-4 h-4 text-zinc-400" />
            <span className="truncate">{attorney.email}</span>
          </div>
        )}
        {attorney.barNumber && (
          <div className="flex items-center gap-2 text-sm text-zinc-600 dark:text-zinc-400">
            <BuildingOfficeIcon className="w-4 h-4 text-zinc-400" />
            <span className="truncate">Bar #{attorney.barNumber}</span>
          </div>
        )}
      </div>

      {/* Case Count */}
      <div className="pt-3 border-t border-zinc-100 dark:border-zinc-800">
        <div className="flex items-center gap-2 text-sm">
          <BriefcaseIcon className="w-4 h-4 text-zinc-400" />
          <span className="text-zinc-600 dark:text-zinc-400">
            {attorney.activeCaseCount || 0} active{" "}
            {attorney.activeCaseCount === 1 ? "case" : "cases"}
          </span>
        </div>
      </div>
    </Link>
  );
};

export default AttorneyGrid;
